/**
 * Authenticated account disclosure (login-password-passkey-ui-plan §2.2.5 · AD-13).
 *
 * A DISCLOSURE — a button whose `aria-expanded` describes a panel — and
 * deliberately not a menu and not a dialog. The distinction is the whole
 * contract:
 *
 *  - **No roving tabindex.** An ARIA menu moves through its items with the
 *    arrow keys and holds exactly one tabbable child. This panel holds ordinary
 *    controls in the ordinary document Tab order, so Tab from the last one
 *    leaves the header into the page.
 *  - **No focus trap, no `aria-modal`.** Containment belongs to the AD-17
 *    set-password confirm (Phase 5), which is a true modal. Building a trap
 *    here would put two competing Tab models one layer apart in the same header.
 *  - **`aria-controls` only while open.** A dangling IDREF is an
 *    `aria-valid-attr-value` violation; `login-panel.tsx` documents the same
 *    trap for `aria-describedby`, and the fix is the same — emit the attribute
 *    on the branch that mounts the target.
 *
 * Mounting is gated on `status === 'authenticated'` rather than on config: the
 * local bypass reports `'bypassed'`, which is not authenticated, so a
 * development build with auth off shows no sign-out control for a session that
 * does not exist.
 *
 * Set password (AD-16) now lands here, and its confirm IS that modal. The panel
 * answers AD-17's "background inert" by NOT RENDERING its other children while
 * the confirm is open, rather than by marking them `aria-hidden`: the attribute
 * over still-focusable controls is an `aria-hidden-focus` violation, while
 * unmounting has no such cost and leaves the confirm's focus trap containing
 * the only controls on screen — so the two Tab models in this header can never
 * both be live. Outside press, the Account trigger, and Escape that did not
 * originate in the dialog are ignored while the confirm is open, so every
 * close goes through SetPassword's dismiss (and its flushSync field clear).
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type JSX,
  type MutableRefObject,
  type RefObject,
} from 'react';

import type { AppConfig } from '@/config';

import styles from './account-menu.module.css';
import { AddPasskey } from './add-passkey';
import { useAuth } from './auth-context';
import { focusIsUnclaimed } from './focus-ownership';
import { SetPassword } from './set-password';

export interface AccountMenuProps {
  readonly config: AppConfig;
  /**
   * Called BEFORE `logout()` is awaited. The shell navigates; this component
   * does not, so its unit tests need no router — the same inversion
   * `PasskeyLogin` uses for `onSuccess`.
   *
   * Returns `unknown` because `useSignOutAction` AWAITS it. That is not
   * decoration: the no-`returnTo` guarantee depends on `/login` being the
   * committed location before the session status flips, and a fire-and-forget
   * navigation only *starts* that transition. `unknown` rather than
   * `void | Promise<…>` because a `void` union is banned
   * (`@typescript-eslint/no-invalid-void-type`) — a host returning nothing
   * satisfies it just as well, and awaiting a non-thenable is a no-op.
   */
  readonly onSignOut?: () => unknown;
}

export function AccountMenu({ config, onSignOut }: AccountMenuProps): JSX.Element | null {
  const { status } = useAuth();
  if (status !== 'authenticated') return null;
  return <AccountDisclosure config={config} {...(onSignOut === undefined ? {} : { onSignOut })} />;
}

/**
 * Focus restoration on CLOSE only — opening moves nothing.
 *
 * The APG Disclosure pattern does not relocate focus on expand, and moving it
 * here actively cost accessibility: the panel's first focusable is the
 * add-passkey button, so an automatic move jumped past the handle (the panel's
 * only identity content, not focusable) and generally suppressed the trigger's
 * own `aria-expanded` announcement. Leaving focus on the trigger means the
 * state change is announced where the user already is, and Tab reaches every
 * control in document order — which is the whole reason this is a disclosure
 * and not a menu.
 *
 * The close arm restores the trigger only when the panel actually held focus
 * (`restoreOnClose`, decided by the caller at dismiss time while the panel is
 * still mounted) or when nobody claimed it. A pointer dismiss that parked
 * focus on another control — ticker search, theme toggle — keeps it there.
 *
 * `wasOpen` is what keeps the close arm from firing on mount. Without it the
 * first render focuses a trigger the user never touched, so merely loading the
 * product would pull focus into the header away from the page.
 */
function useDisclosureFocus(
  open: boolean,
  triggerRef: RefObject<HTMLButtonElement>,
  restoreOnClose: RefObject<boolean>,
): void {
  const wasOpen = useRef(false);
  useEffect(() => {
    if (!open && wasOpen.current && (restoreOnClose.current || focusIsUnclaimed())) {
      triggerRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open, triggerRef, restoreOnClose]);
}

/**
 * Click-outside dismissal (AD-13: "optional but recommended").
 *
 * `mousedown` rather than `click`, because `click` fires on the nearest common
 * ancestor: a press that starts on the handle text and releases past the panel
 * edge dispatches its click on `document` and would read as an outside press.
 */
function useDismissOnOutsidePress(
  open: boolean,
  rootRef: RefObject<HTMLDivElement>,
  onDismiss: () => void,
  blockedRef: MutableRefObject<boolean>,
): void {
  useEffect(() => {
    if (!open) return;
    const handlePress = (event: MouseEvent): void => {
      if (blockedRef.current) return;
      const target = event.target;
      if (target instanceof Node && rootRef.current?.contains(target) === true) return;
      onDismiss();
    };
    document.addEventListener('mousedown', handlePress);
    return () => {
      document.removeEventListener('mousedown', handlePress);
    };
  }, [open, rootRef, onDismiss, blockedRef]);
}

/**
 * Escape closes, from a DOCUMENT listener rather than an `onKeyDown` on the
 * wrapper `<div>`.
 *
 * The second reason is the load-bearing one. `jsx-a11y/no-static-element-
 * interactions` is right that a key handler on a non-focusable div is suspect:
 * it can only ever fire for keys pressed on a descendant. And this disclosure is
 * deliberately NOT a focus trap, so focus may legitimately sit outside the panel
 * while it is still open — a descendant-only handler would leave Escape dead in
 * exactly the state the user most wants it. Registered only while open, so the
 * closed disclosure listens to nothing.
 */
function useDismissOnEscape(
  open: boolean,
  onDismiss: () => void,
  blockedRef: MutableRefObject<boolean>,
): void {
  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent): void => {
      /* The confirm owns Escape outright while it is open — including presses
         that land while focus is outside it, which `set-password.tsx` answers
         with a `document` fallback of its own. This layer must not also close,
         or one keypress collapses two. */
      if (blockedRef.current) return;
      /* A nested control that already consumed Escape wins — a combobox
         following APG closes its own listbox first, and dismissing the panel
         out from under it would collapse two states on one keypress. */
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onDismiss, blockedRef]);
}

/** True while the AD-17 confirm is open — disclosure dismissals must not fire. */
function useConfirmOpenRef(open: boolean): {
  readonly ref: MutableRefObject<boolean>;
  readonly report: (next: boolean) => void;
} {
  const confirmOpenRef = useRef(false);
  useEffect(() => {
    if (!open) confirmOpenRef.current = false;
  }, [open]);
  const report = useCallback((next: boolean) => {
    confirmOpenRef.current = next;
  }, []);
  return { ref: confirmOpenRef, report };
}

function useSignOutAction(
  logout: () => Promise<void>,
  onSignOut: (() => unknown) | undefined,
): () => Promise<void> {
  const signingOut = useRef(false);
  return useCallback(async () => {
    if (signingOut.current) return;
    signingOut.current = true;
    try {
      /*
       * Navigate first — and AWAIT it — so RouteGuard is unmounted before
       * status flips to unauthenticated and tries to stash returnTo.
       *
       * Awaiting is what makes that ordering a guarantee rather than a race.
       * Unawaited, the navigation is merely *started*, and a logout POST that
       * rejects immediately (offline, blocked request) reaches
       * setStatus('unauthenticated') about a microtask later — which can land
       * while RouteGuard is still mounted on the previous route, stashing the
       * page just left as `returnTo`. That is exactly the redirect this
       * ordering exists to prevent.
       *
       * A host that throws must not cancel revocation: the session outliving
       * a failed navigation is the worse of the two failures.
       */
      try {
        await onSignOut?.();
      } catch {
        /* Navigation failed; revoke anyway. */
      }
      await logout();
    } finally {
      signingOut.current = false;
    }
  }, [logout, onSignOut]);
}

function AccountDisclosure({ config, onSignOut }: AccountMenuProps): JSX.Element {
  const { logout, user } = useAuth();
  const signOut = useSignOutAction(logout, onSignOut);
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const restoreOnClose = useRef(true);
  const { ref: confirmOpenRef, report: reportConfirmOpen } = useConfirmOpenRef(open);

  const closeFromPointer = useCallback(() => {
    restoreOnClose.current = false;
    setOpen(false);
  }, []);
  /*
   * Decided HERE, not in the effect: the panel is still mounted at dismiss
   * time, so `contains` can answer whether it held focus. Read one commit
   * later the panel is gone and `activeElement` has already fallen to <body>,
   * which is indistinguishable from focus nobody ever claimed.
   */
  const closeAndRestore = useCallback(() => {
    restoreOnClose.current = panelRef.current?.contains(document.activeElement) === true;
    setOpen(false);
  }, []);
  useDisclosureFocus(open, triggerRef, restoreOnClose);
  useDismissOnOutsidePress(open, rootRef, closeFromPointer, confirmOpenRef);
  /* Escape only. Tab is left UNHANDLED on purpose — intercepting it is exactly
     how a disclosure becomes the trap AD-13 forbids. While the confirm is open
     the disclosure does not dismiss at all — AD-17 owns that layer. */
  useDismissOnEscape(open, closeAndRestore, confirmOpenRef);

  return (
    <div className={styles['root']} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles['trigger']}
        aria-expanded={open}
        {...(open ? { 'aria-controls': panelId } : {})}
        onClick={() => {
          if (confirmOpenRef.current) return;
          restoreOnClose.current = true;
          setOpen((previous) => !previous);
        }}
      >
        Account
      </button>
      {open ? (
        <AccountPanel
          id={panelId}
          panelRef={panelRef}
          handle={user?.handle ?? null}
          config={config}
          onSignOut={() => void signOut()}
          onConfirmOpenChange={reportConfirmOpen}
        />
      ) : null}
    </div>
  );
}

interface AccountPanelProps {
  readonly id: string;
  readonly panelRef: RefObject<HTMLDivElement>;
  readonly handle: string | null;
  readonly config: AppConfig;
  readonly onSignOut: () => void;
  readonly onConfirmOpenChange: (open: boolean) => void;
}

/**
 * `data-account-panel` is the stable hook, for the reason `theme-toggle.tsx`
 * documents: the panel carries no role and no accessible name of its own — it
 * is a plain container, which is the point — so there is nothing semantic for a
 * spec to bind to without inventing semantics the pattern does not have.
 */
function AccountPanel({
  id,
  panelRef,
  handle,
  config,
  onSignOut,
  onConfirmOpenChange,
}: AccountPanelProps): JSX.Element {
  /* Owned here, not by SetPassword, because the state's whole purpose is what
     the SIBLINGS do with it — the confirm reports its open state outward and
     the panel answers by unmounting everything else (see the file header).
     `onConfirmOpenChange` is the disclosure's copy: outside press / trigger /
     Escape must not tear this tree down without going through dismiss. */
  const [confirmOpen, setConfirmOpen] = useState(false);
  const reportConfirm = useCallback(
    (next: boolean) => {
      onConfirmOpenChange(next);
      setConfirmOpen(next);
    },
    [onConfirmOpenChange],
  );
  return (
    <div id={id} ref={panelRef} className={styles['panel']} data-account-panel="">
      {confirmOpen ? null : (
        <>
          {/* Guarded, not defaulted: `authenticatedUser` (auth-context.tsx)
              treats a missing handle as NOT authenticated, so this panel never
              renders without one today. The branch is here because that
              guarantee belongs to another module — style §4 asks for a guard,
              not an assumption, when the invariant is not local. */}
          {handle === null ? null : <p className={styles['handle']}>{handle}</p>}
          <AddPasskey config={config} />
        </>
      )}
      {/* The one child that survives: it is the confirm's own container, and
          unmounting it would take the dialog with it. */}
      <SetPassword config={config} onOpenChange={reportConfirm} />
      {confirmOpen ? null : (
        <button type="button" className={styles['action']} onClick={onSignOut}>
          Sign out
        </button>
      )}
    </div>
  );
}
